import type {
  BriefResponse,
  CaseCard,
  CaseCardResponse,
  CompanyId,
  ConfidenceAudit,
  GraphEdge,
  GraphNode,
  MaterialChange,
  PortfolioResponse,
  PropagationRequest,
} from "../types";
import { companyLabel, formatMetricLabel } from "./utils";

interface CompanyRecord {
  id: CompanyId;
  ticker: string;
  name: string;
  sector: string;
}

interface EdgeSeed {
  source: CompanyId;
  target: CompanyId;
  source_metric: string;
  target_metric: string;
  direction: GraphEdge["direction"];
  confidence: number;
  prior_confidence: number;
  mechanism: string;
  n_observations: number;
  historical_match_rate: number | null;
  // Optional — only included on a few representative edges so you can see the
  // audit trail in action during dev. The real notebook populates this for all edges.
  confidence_audit?: ConfidenceAudit;
}

export const COMPANIES: CompanyRecord[] = [
  {
    id: "apple",
    ticker: "AAPL",
    name: "Apple Inc.",
    sector: "Consumer Electronics + Services",
  },
  {
    id: "nvidia",
    ticker: "NVDA",
    name: "NVIDIA Corporation",
    sector: "Semiconductors (GPU, AI Accelerators)",
  },
  {
    id: "tsmc",
    ticker: "TSM",
    name: "Taiwan Semiconductor Manufacturing Co.",
    sector: "Semiconductor Foundry",
  },
  {
    id: "google",
    ticker: "GOOGL",
    name: "Alphabet Inc.",
    sector: "Advertising + Cloud + AI",
  },
  {
    id: "microsoft",
    ticker: "MSFT",
    name: "Microsoft Corporation",
    sector: "Cloud + Enterprise Software + AI",
  },
];

const EDGE_SEEDS: EdgeSeed[] = [
  {
    source: "apple",
    target: "tsmc",
    source_metric: "r_and_d",
    target_metric: "wafer_revenue",
    direction: "positive",
    confidence: 0.81,
    prior_confidence: 0.77,
    mechanism: "Apple design ramps convert into advanced-node wafer demand at TSMC.",
    n_observations: 8,
    historical_match_rate: 0.75,
    // Mock audit trail: 3 factors that were multiplied together to produce 0.81
    confidence_audit: {
      steps: [
        { label: "Base Prior",            value: 0.77, note: "Prior confidence from last run (FY2025)" },
        { label: "Historical Match Rate", value: 0.75, note: "6 of 8 observations confirmed this link" },
        { label: "Mechanism Evidence",    value: 0.90, note: "Apple 10-K explicitly names TSMC as sole foundry partner" },
      ],
      formula: "0.77 × 0.60 + 0.75 × 0.25 + 0.90 × 0.15 = 0.81",
      observations_matched: 6,
      observations_total: 8,
    },
  },
  {
    source: "apple",
    target: "nvidia",
    source_metric: "ai_capex",
    target_metric: "data_center_revenue",
    direction: "positive",
    confidence: 0.69,
    prior_confidence: 0.63,
    mechanism: "Apple AI infrastructure buildouts tighten GPU supply and reinforce accelerator demand.",
    n_observations: 5,
    historical_match_rate: 0.6,
  },
  {
    source: "apple",
    target: "google",
    source_metric: "services_revenue",
    target_metric: "traffic_acquisition_cost",
    direction: "complex",
    confidence: 0.58,
    prior_confidence: 0.55,
    mechanism: "Safari distribution economics tie Apple services scale to Google TAC and search monetization.",
    n_observations: 6,
    historical_match_rate: 0.52,
  },
  {
    source: "apple",
    target: "microsoft",
    source_metric: "enterprise_adoption",
    target_metric: "m365_seat_growth",
    direction: "complex",
    confidence: 0.41,
    prior_confidence: 0.39,
    mechanism: "Enterprise Apple deployments can alter collaboration software usage without a single linear effect.",
    n_observations: 4,
    historical_match_rate: 0.44,
  },
  {
    source: "nvidia",
    target: "tsmc",
    source_metric: "data_center_revenue",
    target_metric: "advanced_packaging_revenue",
    direction: "positive",
    confidence: 0.88,
    prior_confidence: 0.84,
    mechanism: "NVIDIA GPU demand directly amplifies CoWoS and advanced packaging utilization at TSMC.",
    n_observations: 7,
    historical_match_rate: 0.79,
    // Mock audit trail for the strongest edge in the graph
    confidence_audit: {
      steps: [
        { label: "Base Prior",            value: 0.84, note: "Prior confidence from last run (FY2025)" },
        { label: "Historical Match Rate", value: 0.79, note: "5.5 of 7 observations confirmed this link" },
        { label: "Disclosed Relation",    value: 1.00, note: "TSMC FY2024 10-K names NVIDIA as top-3 customer by revenue" },
        { label: "Revenue Concentration", value: 0.91, note: "NVDA = ~11% of TSMC advanced packaging revenue" },
      ],
      formula: "0.84 × 0.50 + 0.79 × 0.20 + 1.00 × 0.20 + 0.91 × 0.10 = 0.88",
      observations_matched: 6,
      observations_total: 7,
    },
  },
  {
    source: "nvidia",
    target: "microsoft",
    source_metric: "gpu_supply",
    target_metric: "azure_ai_growth",
    direction: "positive",
    confidence: 0.71,
    prior_confidence: 0.65,
    mechanism: "GPU availability shapes Azure AI service capacity and monetization cadence.",
    n_observations: 6,
    historical_match_rate: 0.63,
  },
  {
    source: "nvidia",
    target: "google",
    source_metric: "ai_pricing",
    target_metric: "cloud_margin",
    direction: "negative",
    confidence: 0.54,
    prior_confidence: 0.49,
    mechanism: "Higher accelerator pricing can compress gross margins for model-heavy cloud workloads.",
    n_observations: 4,
    historical_match_rate: 0.47,
  },
  {
    source: "nvidia",
    target: "apple",
    source_metric: "ai_reference_designs",
    target_metric: "on_device_ai_spend",
    direction: "positive",
    confidence: 0.38,
    prior_confidence: 0.35,
    mechanism: "Breakthroughs in AI tooling can raise the baseline for on-device model ambitions across the sector.",
    n_observations: 3,
    historical_match_rate: 0.4,
  },
  {
    source: "tsmc",
    target: "apple",
    source_metric: "yield_curve",
    target_metric: "gross_margin",
    direction: "positive",
    confidence: 0.74,
    prior_confidence: 0.71,
    mechanism: "Foundry yield improvements lower unit costs on Apple silicon programs and support hardware margin.",
    n_observations: 9,
    historical_match_rate: 0.68,
  },
  {
    source: "tsmc",
    target: "nvidia",
    source_metric: "capacity_mix",
    target_metric: "gpu_shipments",
    direction: "positive",
    confidence: 0.83,
    prior_confidence: 0.77,
    mechanism: "TSMC capacity allocation is a first-order driver of NVIDIA shipment volume.",
    n_observations: 8,
    historical_match_rate: 0.72,
  },
  {
    source: "tsmc",
    target: "google",
    source_metric: "packaging_capacity",
    target_metric: "tpu_rollout",
    direction: "positive",
    confidence: 0.61,
    prior_confidence: 0.58,
    mechanism: "Packaging and substrate availability gates TPU deployment velocity.",
    n_observations: 4,
    historical_match_rate: 0.56,
  },
  {
    source: "google",
    target: "apple",
    source_metric: "search_monetization",
    target_metric: "services_revenue",
    direction: "positive",
    confidence: 0.63,
    prior_confidence: 0.57,
    mechanism: "Default search economics and revenue sharing remain a meaningful Apple services lever.",
    n_observations: 8,
    historical_match_rate: 0.61,
  },
  {
    source: "google",
    target: "microsoft",
    source_metric: "genai_competition",
    target_metric: "copilot_attach",
    direction: "positive",
    confidence: 0.57,
    prior_confidence: 0.51,
    mechanism: "Competitive AI releases can accelerate enterprise experimentation and software attach rates.",
    n_observations: 4,
    historical_match_rate: 0.48,
  },
  {
    source: "google",
    target: "nvidia",
    source_metric: "cloud_ai_demand",
    target_metric: "accelerator_orders",
    direction: "positive",
    confidence: 0.79,
    prior_confidence: 0.74,
    mechanism: "Google AI demand feeds directly into accelerator order cycles.",
    n_observations: 6,
    historical_match_rate: 0.69,
  },
  {
    source: "microsoft",
    target: "nvidia",
    source_metric: "azure_ai_growth",
    target_metric: "gpu_orders",
    direction: "positive",
    confidence: 0.86,
    prior_confidence: 0.8,
    mechanism: "Azure AI demand remains a major pull-through vector for NVIDIA accelerators.",
    n_observations: 7,
    historical_match_rate: 0.74,
  },
  {
    source: "microsoft",
    target: "google",
    source_metric: "copilot_attach",
    target_metric: "workspace_pricing",
    direction: "complex",
    confidence: 0.44,
    prior_confidence: 0.4,
    mechanism: "Copilot monetization changes the pricing umbrella for competing productivity suites.",
    n_observations: 3,
    historical_match_rate: 0.39,
  },
  {
    source: "microsoft",
    target: "apple",
    source_metric: "productivity_ai",
    target_metric: "device_refresh_rate",
    direction: "positive",
    confidence: 0.37,
    prior_confidence: 0.34,
    mechanism: "Enterprise AI features can indirectly support premium device refresh behavior.",
    n_observations: 3,
    historical_match_rate: 0.35,
  },
];

// Lazy-loaded material-change seeds. `auditData.ts` is a ~725 KB static
// pipeline snapshot; we keep it out of the main bundle by dynamically
// importing it only when a mock portfolio is actually built (i.e. when the
// live API is unreachable and we need a fallback).
let materialChangeSeedsPromise: Promise<Record<CompanyId, MaterialChange[]>> | null = null;

function loadMaterialChangeSeeds(): Promise<Record<CompanyId, MaterialChange[]>> {
  if (!materialChangeSeedsPromise) {
    materialChangeSeedsPromise = import(
      /* webpackChunkName: "audit-data" */ "./auditData"
    ).then(({ PIPELINE_OUTPUT }) =>
      Object.fromEntries(
        Object.entries(PIPELINE_OUTPUT.companies).map(([id, company]) => [
          id,
          ((company as unknown as { material_changes?: Array<{ metric: string; direction: "increased" | "decreased"; pct?: number; pct_change?: number; audit?: MaterialChange["audit"] }> }).material_changes ?? [])
            .slice(0, 4)
            .map((mc) => ({
              metric: mc.metric,
              direction: mc.direction,
              pct: mc.pct ?? mc.pct_change ?? 0,
              ...(mc.audit ? { audit: mc.audit } : {}),
            })),
        ])
      ) as Record<CompanyId, MaterialChange[]>
    );
  }
  return materialChangeSeedsPromise;
}

function buildNodes(
  materialChangeSeeds: Record<CompanyId, MaterialChange[]>,
  triggerCompany?: CompanyId,
  effectsByTarget?: Map<CompanyId, GraphNode["predicted_effects"]>
): GraphNode[] {
  return COMPANIES.map((company) => ({
    id: company.id,
    ticker: company.ticker,
    name: company.name,
    sector: company.sector,
    is_trigger: company.id === triggerCompany,
    material_changes:
      company.id === triggerCompany ? materialChangeSeeds[company.id] ?? [] : [],
    predicted_effects: effectsByTarget?.get(company.id) ?? [],
  }));
}

function buildInactiveEdges(): GraphEdge[] {
  return EDGE_SEEDS.map((edge) => ({
    ...edge,
    is_active: false,
    effects: [],
  }));
}

function buildActiveEffects(edge: EdgeSeed): GraphEdge["effects"] {
  const target = companyLabel(edge.target);
  const sourceMetric = formatMetricLabel(edge.source_metric);
  const targetMetric = formatMetricLabel(edge.target_metric);
  const directionVerb =
    edge.direction === "negative"
      ? "pressure"
      : edge.direction === "complex"
      ? "re-rate"
      : "lift";

  // The threshold % used to detect the trigger change (same value as thresholdPct in the request).
  // In mock mode we use 5% as a stand-in; the real notebook passes the actual value.
  const mockThreshold = 5.0;

  return [
    {
      text: `${target} ${targetMetric} could ${directionVerb} as ${sourceMetric} trends propagate through the portfolio.`,
      confidence: Math.max(0.35, edge.confidence - 0.02),
      layer: 3,
      // Show exactly what rule fired and what threshold was crossed
      audit: {
        triggered_by: `${edge.source.toUpperCase()} ${edge.source_metric} ↑ ${(mockThreshold * 1.8).toFixed(1)}%`,
        rule: `L3: Portfolio hypothesis — if ${sourceMetric} exceeds threshold, ${targetMetric} may ${directionVerb}`,
        threshold_crossed: `${(mockThreshold * 1.8).toFixed(1)}% > ${mockThreshold}% minimum`,
      },
    },
    {
      text: `${target} management commentary is likely to reference the ${sourceMetric.toLowerCase()} channel in the next filing cycle.`,
      confidence: Math.max(0.33, edge.confidence - 0.08),
      layer: 3,
      audit: {
        triggered_by: `${edge.source.toUpperCase()} ${edge.source_metric} ↑ ${(mockThreshold * 1.8).toFixed(1)}%`,
        rule: `L3: Language propagation — material ${sourceMetric} moves typically surface in partner MD&A`,
        threshold_crossed: `${(mockThreshold * 1.8).toFixed(1)}% > ${mockThreshold}% minimum`,
      },
    },
    {
      text: `Consensus expectations for ${targetMetric.toLowerCase()} may lag the causal signal embedded in ${edge.source_metric}.`,
      confidence: Math.max(0.31, edge.confidence - 0.12),
      layer: 3,
      audit: {
        triggered_by: `${edge.source.toUpperCase()} ${edge.source_metric} ↑ ${(mockThreshold * 1.8).toFixed(1)}%`,
        rule: `L3: Consensus lag hypothesis — sell-side models update slowly to supply-chain signals`,
        threshold_crossed: `${(mockThreshold * 1.8).toFixed(1)}% > ${mockThreshold}% minimum`,
      },
    },
    {
      text: `Historical case cards imply the transmission path remains live despite sector-specific confounders.`,
      confidence: Math.max(0.29, edge.confidence - 0.16),
      layer: 3,
      audit: {
        triggered_by: `${edge.source.toUpperCase()} ${edge.source_metric} ↑ ${(mockThreshold * 1.8).toFixed(1)}%`,
        rule: `L3: Historical precedent — ${edge.n_observations} prior observations show this path active`,
        threshold_crossed: `Match rate ${Math.round((edge.historical_match_rate ?? 0.5) * 100)}% exceeds 40% hurdle`,
      },
    },
  ];
}

export async function createMockPortfolio(): Promise<PortfolioResponse> {
  const materialChangeSeeds = await loadMaterialChangeSeeds();
  return {
    nodes: buildNodes(materialChangeSeeds),
    edges: buildInactiveEdges(),
    propagation_summary: null,
  };
}

export async function createMockPropagation(
  request: PropagationRequest
): Promise<PortfolioResponse> {
  const materialChangeSeeds = await loadMaterialChangeSeeds();
  const activeSeeds = EDGE_SEEDS.filter((edge) => edge.source === request.company)
    .sort((left, right) => right.confidence - left.confidence)
    .slice(0, 4);

  const effectsByTarget = new Map<CompanyId, GraphNode["predicted_effects"]>();

  const edges = EDGE_SEEDS.map((seed) => {
    const isActive = activeSeeds.some(
      (active) =>
        active.source === seed.source &&
        active.target === seed.target &&
        active.source_metric === seed.source_metric &&
        active.target_metric === seed.target_metric
    );

    const effects = isActive ? buildActiveEffects(seed) : [];

    if (isActive) {
      effectsByTarget.set(
        seed.target,
        effects.map((effect) => ({
          effect_text: effect.text,
          confidence: effect.confidence,
          layer: effect.layer,
          trigger_metric: seed.source_metric,
          trigger_company: seed.source,
        }))
      );
    }

    return {
      ...seed,
      is_active: isActive,
      effects,
    };
  });

  const nodes = buildNodes(materialChangeSeeds, request.company, effectsByTarget);
  const triggerTicker = COMPANIES.find((company) => company.id === request.company)?.ticker ?? "AAPL";

  return {
    nodes,
    edges,
    propagation_summary: {
      trigger_company: triggerTicker,
      trigger_filing: `FY${request.fyCurrent}`,
      total_effects: 75,
      depth1_effects: 59,
      depth2_effects: activeSeeds.length * 4,
      scenario: request.scenario,
    },
  };
}

export function createMockBrief(
  request: PropagationRequest,
  graph: PortfolioResponse
): BriefResponse {
  const company = COMPANIES.find((entry) => entry.id === request.company);
  const triggerNode = graph.nodes.find((node) => node.is_trigger);
  const targetEffects = graph.edges
    .filter((edge) => edge.is_active)
    .flatMap((edge) =>
      edge.effects.map(
        (effect) =>
          `  📄 [${Math.round(effect.confidence * 100)}%] ${companyLabel(
            typeof edge.target === "string" ? edge.target : edge.target.id
          )}: ${effect.text}`
      )
    )
    .slice(0, 6);

  const changes = (triggerNode?.material_changes ?? [])
    .map(
      (change) =>
        `  ${change.direction === "decreased" ? "↓" : "↑"} ${formatMetricLabel(
          change.metric
        )}: ${change.direction} ${change.pct.toFixed(1)}%`
    )
    .join("\n");

  return {
    filename: `effect_impact_brief_${company?.ticker ?? "AAPL"}_FY${request.fyCurrent}.txt`,
    brief: [
      "══════════════════════════════════════════════════════════════",
      `EFFECT IMPACT BRIEF — ${company?.name ?? "Apple Inc."}`,
      `Scenario: ${request.scenario} | FY${request.fyCurrent} vs FY${request.fyPrior}`,
      "Generated: Mock fallback dataset",
      "══════════════════════════════════════════════════════════════",
      "",
      "1. MATERIAL CHANGES DETECTED",
      "────────────────────────────",
      changes || "  No material changes available.",
      "",
      `2. DIRECT EFFECTS (${graph.propagation_summary?.depth1_effects ?? 0} found)`,
      "────────────────────────────",
      "  ✅ [99%] Structural operating leverage remains intact on the trigger company.",
      "  ✅ [96%] Expense mix changes alter margin sensitivity and EPS conversion.",
      "  ✅ [91%] Narrative cues reinforce the quantitative signal in the filing.",
      "",
      `3. PORTFOLIO IMPLICATIONS (${graph.propagation_summary?.depth2_effects ?? 0} found)`,
      "────────────────────────────",
      ...targetEffects,
      "",
      "4. EXECUTIVE TAKEAWAY",
      "────────────────────────────",
      `${company?.ticker ?? "AAPL"} is acting as the trigger node in this run. The strongest portfolio transmission paths concentrate in semiconductors, infrastructure, and AI platform monetization.`,
    ].join("\n"),
  };
}

export function createMockCaseCards(
  source: string,
  metric: string,
  target: string
): CaseCardResponse {
  const sourceLabel = companyLabel(source);
  const targetLabel = companyLabel(target);
  const metricLabel = formatMetricLabel(metric);

  const cards: CaseCard[] = [
    {
      case_id: `${source.toUpperCase()}_${metric.toUpperCase()}_${target.toUpperCase()}_FY2024`,
      trigger_company: source,
      trigger_metric: metric,
      trigger_direction: "increased",
      trigger_magnitude_pct: 11.2,
      trigger_fiscal_year: "2024",
      effect_company: target,
      effect_metric: "forward_signal",
      effect_direction: "increased",
      effect_magnitude_pct: 18.4,
      mechanism: `${sourceLabel} ${metricLabel.toLowerCase()} shifted supplier and platform economics in a way that later surfaced in ${targetLabel}'s reported metrics.`,
      confounders: ["Macro demand softened partway through the observation window."],
      counterexample_note: `${targetLabel} decoupled in FY2023 when channel inventory dominated the transmission path.`,
    },
    {
      case_id: `${source.toUpperCase()}_${metric.toUpperCase()}_${target.toUpperCase()}_FY2022`,
      trigger_company: source,
      trigger_metric: metric,
      trigger_direction: "decreased",
      trigger_magnitude_pct: 7.1,
      trigger_fiscal_year: "2022",
      effect_company: target,
      effect_metric: "portfolio_signal",
      effect_direction: "decreased",
      effect_magnitude_pct: 9.6,
      mechanism: `Historical co-movement suggests ${sourceLabel} can reshape expectations for ${targetLabel} through the ${metricLabel.toLowerCase()} channel with a one-to-two quarter lag.`,
      confounders: ["Competing product cycles weakened the clean read-through."],
      counterexample_note: null,
    },
  ];

  return { cards, count: cards.length };
}
